import { Injectable } from '@angular/core';
import {
  bytesToBase64,
  generateUUID
} from '@com-delta-idp/mnx-sitewide-shared-utils';
import { sha256 } from '@noble/hashes/sha2.js';
import {
  CLIENT_ID_VALUES,
  LOGIN_URLS
} from '../../constants/login/login-remember-me-constant';
import {
  OauthAEMConfig,
  PkceCode,
  SwapAuthForAccessTokens
} from '../../models/login';
import { LoginRememberMeUtilsHelperService } from './login-remember-me-utils-helper.service';

@Injectable({
  providedIn: 'root'
})
export class LoginRememberMeUtilsService extends LoginRememberMeUtilsHelperService {
  generateCodeChallenge(): PkceCode {
    const codeVerifier = this.generateStrRandomForCodeChallenge(128);
    const codeVerifierHash = bytesToBase64(
      sha256(new TextEncoder().encode(codeVerifier))
    );
    const codeChallenge = codeVerifierHash
      .replace(/=/g, '')
      .replace(/\+/g, '-')
      .replace(/\//g, '_');
    const code = { codeVerifier, codeChallenge };
    return code;
  }

  getPingServiceUrl(
    oauthConfig: OauthAEMConfig | undefined,
    code?: string,
    codeChallenge?: string
  ) {
    const pingEnv = oauthConfig?.PING_DOMAIN;
    const pingUrl = {
      loginOAuthUrl: `${pingEnv}${oauthConfig?.OAUTH2}${oauthConfig?.CLIENT_ID}=${oauthConfig?.CLIENT_ID_VALUE}&${oauthConfig?.RESPONSE_TYPE}=${oauthConfig?.RESPONSE_TYPE_VALUE}&${oauthConfig?.SCOPE}=${oauthConfig?.SCOPE_VALUE}&${oauthConfig?.RESPONSE_MODE}=${oauthConfig?.RESPONSE_MODE_VALUE}&${oauthConfig?.CODE_CHALLENGE_METHOD}=${oauthConfig?.CODE_CHALLENGE_METHOD_VALUE}&${oauthConfig?.CODE_CHALLENGE}=${codeChallenge}`,
      loginOAuthUrlRememberMe: `${pingEnv}${oauthConfig?.OAUTH2}${oauthConfig?.CLIENT_ID}=${oauthConfig?.CLIENT_ID_VALUE}&${oauthConfig?.RESPONSE_TYPE}=${oauthConfig?.RESPONSE_TYPE_VALUE}&${oauthConfig?.SCOPE}=${oauthConfig?.SCOPE_VALUE_RM}&${oauthConfig?.RESPONSE_MODE}=${oauthConfig?.RESPONSE_MODE_VALUE}&${oauthConfig?.CODE_CHALLENGE_METHOD}=${oauthConfig?.CODE_CHALLENGE_METHOD_VALUE}&${oauthConfig?.CODE_CHALLENGE}=${codeChallenge}&${oauthConfig?.VERSION_PARAMETER}=${oauthConfig?.VERSION_PARAMETER_VALUE}`,
      loginOAuthUrlRbaLearning: `${pingEnv}${oauthConfig?.OAUTH2}${oauthConfig?.CLIENT_ID}=${oauthConfig?.CLIENT_ID_VALUE}&${oauthConfig?.RESPONSE_TYPE}=${oauthConfig?.RESPONSE_TYPE_VALUE}&${oauthConfig?.SCOPE}=${oauthConfig?.SCOPE_VALUE_RM}&${oauthConfig?.RESPONSE_MODE}=${oauthConfig?.RESPONSE_MODE_VALUE}&${oauthConfig?.CODE_CHALLENGE_METHOD}=${oauthConfig?.CODE_CHALLENGE_METHOD_VALUE}&${oauthConfig?.CODE_CHALLENGE}=${codeChallenge}&${oauthConfig?.VERSION_PARAMETER}=${oauthConfig?.VERSION_PARAMETER_RBA_LEARNING_VALUE}`,
      loginOAuthUrlRbaLogin: `${pingEnv}${oauthConfig?.OAUTH2}${oauthConfig?.CLIENT_ID}=${oauthConfig?.CLIENT_ID_VALUE}&${oauthConfig?.RESPONSE_TYPE}=${oauthConfig?.RESPONSE_TYPE_VALUE}&${oauthConfig?.SCOPE}=${oauthConfig?.SCOPE_VALUE_RM}&${oauthConfig?.RESPONSE_MODE}=${oauthConfig?.RESPONSE_MODE_VALUE}&${oauthConfig?.CODE_CHALLENGE_METHOD}=${oauthConfig?.CODE_CHALLENGE_METHOD_VALUE}&${oauthConfig?.CODE_CHALLENGE}=${codeChallenge}&${oauthConfig?.VERSION_PARAMETER}=${oauthConfig?.VERSION_PARAMETER_RBA_LOGIN_VALUE}`,
      loginOAuthUrlPersistant: `${pingEnv}${oauthConfig?.OAUTH2}${oauthConfig?.CLIENT_ID}=${oauthConfig?.CLIENT_ID_VALUE}&${oauthConfig?.RESPONSE_TYPE}=${oauthConfig?.RESPONSE_TYPE_VALUE}&${oauthConfig?.SCOPE}=${oauthConfig?.SCOPE_VALUE}&${oauthConfig?.RESPONSE_MODE}=${oauthConfig?.RESPONSE_MODE_VALUE}&${oauthConfig?.CODE_CHALLENGE_METHOD}=${oauthConfig?.CODE_CHALLENGE_METHOD_VALUE}&${oauthConfig?.CODE_CHALLENGE}=${codeChallenge}&${oauthConfig?.PROMPT}=${oauthConfig?.PROMPT_VALUE}`,
      loginOAuthUrlPersistantRememberMe: `${pingEnv}${oauthConfig?.OAUTH2}${oauthConfig?.CLIENT_ID}=${oauthConfig?.CLIENT_ID_VALUE}&${oauthConfig?.RESPONSE_TYPE}=${oauthConfig?.RESPONSE_TYPE_VALUE}&${oauthConfig?.SCOPE}=${oauthConfig?.SCOPE_VALUE}&${oauthConfig?.RESPONSE_MODE}=${oauthConfig?.RESPONSE_MODE_VALUE}&${oauthConfig?.CODE_CHALLENGE_METHOD}=${oauthConfig?.CODE_CHALLENGE_METHOD_VALUE}&${oauthConfig?.CODE_CHALLENGE}=${codeChallenge}&${oauthConfig?.VERSION_PARAMETER}=${oauthConfig?.VERSION_PARAMETER_VALUE}&${oauthConfig?.PROMPT}=${oauthConfig?.PROMPT_VALUE}`,
      mfaLoginOAuthUrl: `${pingEnv}${oauthConfig?.OAUTH2}${oauthConfig?.CLIENT_ID}=${oauthConfig?.MFA_CLIENT_ID_VALUE}&${oauthConfig?.RESPONSE_TYPE}=${oauthConfig?.RESPONSE_TYPE_VALUE}&${oauthConfig?.SCOPE}=${oauthConfig?.SCOPE_VALUE}&${oauthConfig?.RESPONSE_MODE}=${oauthConfig?.RESPONSE_MODE_VALUE}&${oauthConfig?.CODE_CHALLENGE_METHOD}=${oauthConfig?.CODE_CHALLENGE_METHOD_VALUE}&${oauthConfig?.CODE_CHALLENGE}=${codeChallenge}&${oauthConfig?.MFA_ACTION}=${oauthConfig?.MFA_ACTION_VALUE}&${oauthConfig?.VERSION_PARAMETER}=${oauthConfig?.VERSION_PARAMETER_VALUE}`,
      newUserMfaLoginOAuthUrlWithoutEmailValidation: `${pingEnv}${oauthConfig?.OAUTH2}${oauthConfig?.CLIENT_ID}=${oauthConfig?.MFA_CLIENT_ID_VALUE}&${oauthConfig?.RESPONSE_TYPE}=${oauthConfig?.RESPONSE_TYPE_VALUE}&${oauthConfig?.SCOPE}=${oauthConfig?.SCOPE_VALUE}&${oauthConfig?.RESPONSE_MODE}=${oauthConfig?.RESPONSE_MODE_VALUE}&${oauthConfig?.CODE_CHALLENGE_METHOD}=${oauthConfig?.CODE_CHALLENGE_METHOD_VALUE}&${oauthConfig?.CODE_CHALLENGE}=${codeChallenge}&${oauthConfig?.VERSION_PARAMETER}=${oauthConfig?.VERSION_PARAMETER_VALUE}`,
      newUserMfaLoginOAuthUrlEmailValidation: `${pingEnv}${oauthConfig?.OAUTH2}${oauthConfig?.CLIENT_ID}=${oauthConfig?.MFA_CLIENT_ID_VALUE}&${oauthConfig?.RESPONSE_TYPE}=${oauthConfig?.RESPONSE_TYPE_VALUE}&${oauthConfig?.SCOPE}=${oauthConfig?.SCOPE_VALUE}&${oauthConfig?.RESPONSE_MODE}=${oauthConfig?.RESPONSE_MODE_VALUE}&${oauthConfig?.CODE_CHALLENGE_METHOD}=${oauthConfig?.CODE_CHALLENGE_METHOD_VALUE}&${oauthConfig?.CODE_CHALLENGE}=${codeChallenge}&${oauthConfig?.VERIFY_EMAIL_MFA}=${oauthConfig?.VERIFY_EMAIL_MFA_VALUE}`,
      selfServiceResetLoginOAuthUrl: `${pingEnv}${oauthConfig?.OAUTH2}${oauthConfig?.CLIENT_ID}=${oauthConfig?.MFA_CLIENT_ID_VALUE}&${oauthConfig?.RESPONSE_TYPE}=${oauthConfig?.RESPONSE_TYPE_VALUE}&${oauthConfig?.SCOPE}=${oauthConfig?.SCOPE_VALUE}&${oauthConfig?.RESPONSE_MODE}=${oauthConfig?.RESPONSE_MODE_VALUE}&${oauthConfig?.CODE_CHALLENGE_METHOD}=${oauthConfig?.CODE_CHALLENGE_METHOD_VALUE}&${oauthConfig?.CODE_CHALLENGE}=${codeChallenge}&${oauthConfig?.MFA_ACTION}=${oauthConfig?.MFA_ACTION_SSPR_VALUE}&${oauthConfig?.VERSION_PARAMETER}=${oauthConfig?.VERSION_PARAMETER_VALUE}`,
      loginAuthWithCredentialsUrl: `${pingEnv}${oauthConfig?.OAUTH2_USER_CREDENTIALS}${code}`,
      swapAuthForAccessTokensUrl: `${pingEnv}${oauthConfig?.OAUTH2_TOKEN}`,
      body: `${oauthConfig?.GRANT_TYPE}=${oauthConfig?.AUTHORIZATION_CODE}&${oauthConfig?.CODE}=${code}&${oauthConfig?.CODE_VERIFIER}=${codeChallenge}&${oauthConfig?.CLIENT_ID}=${oauthConfig?.CLIENT_ID_VALUE}`,
      loginOAuthRedirectfulUrl: `${pingEnv}${oauthConfig?.OAUTH2_USER_CREDENTIALS}${code}`,
      introspectionUrl: `${pingEnv}${oauthConfig?.INTROSPECTION}`
    };
    return pingUrl;
  }

  setLocalStorageValue(key: string, value: string) {
    let result = false;
    if (this.isLocalStorageSupported()) {
      localStorage.setItem(key, value);
      result = true;
    }
    return result;
  }

  generateUniqueIdentifier(): string {
    return generateUUID();
  }

  generateStrRandomForCodeChallenge(length: number): string {
    let result = '';
    const characters =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  }

  sendTokenServiceWorker(
    config: OauthAEMConfig,
    tokenResponse: SwapAuthForAccessTokens
  ) {
    if (
      config?.ENABLE_NEW_PERSISTENT_LOGIN &&
      this.checkServiceWorkerController() &&
      this.validateClientIdValues(config)
    ) {
      this.sendMessage({
        type: 'ADD_TOKEN',
        token: tokenResponse?.access_token,
        issueTime: new Date().getTime(),
        expiresIn: tokenResponse?.expires_in
      });
      this.sendMessage({ type: 'DELETE_STATUS' });
    }
  }

  validateClientIdValues(config: OauthAEMConfig): boolean {
    return CLIENT_ID_VALUES.includes(config.CLIENT_ID_VALUE);
  }

  sendMessage(message: {
    type: string;
    token?: string;
    issueTime?: number;
    expiresIn?: number;
    eamStatus?: string;
    remSec?: number;
    headerToken?: string;
    headerIssueTime?: number;
    headerExpiresIn?: number;
  }) {
    return new Promise(function (resolve, reject) {
      const messageChannel = new MessageChannel();
      messageChannel.port1.onmessage = function (event) {
        if (event.data.error) {
          reject(event.data.error);
        } else {
          resolve(event.data);
        }
      };
      if (navigator.serviceWorker && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage(message, [
          messageChannel.port2
        ]);
      }
    });
  }

  checkLoginUrls(): boolean {
    const loginUrls = LOGIN_URLS;
    return (
      loginUrls.some((item) => window.location.href?.includes(item)) ?? false
    );
  }

  checkServiceWorkerController(): boolean {
    return !!(navigator.serviceWorker && navigator.serviceWorker.controller);
  }
}
